import React, { useEffect, useState } from "react";
import { ShieldAlert, X, AlertTriangle } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

/**
 * UploadBlockedModal (Phase 9.6 v2)
 *
 * Modal di errore mostrato quando il backend risponde 422 durante upload
 * documento per **P.IVA mismatch** rilevata nel PDF. L'upload è stato
 * RIFIUTATO server-side.
 *
 * Se l'utente è admin/super_admin, il modal mostra una sezione "Forza
 * caricamento (override admin)" con textarea motivazione (min 10 char) e
 * pulsante di override. Il click ri-invia l'upload con `override_block=true`
 * + `override_reason`, il backend registra l'evento in audit trail.
 *
 * Props:
 * - detail: { message, blocked_client:{id,company_name}, match_type, evidence,
 *             admin_override_available }
 * - filename: str
 * - file: File | null — necessario per override re-submit (se null, override disabilitato)
 * - uploadContext: { clientId, name, type, visible, notes, categoryId }
 *   → tutti i campi form necessari per il re-invio
 * - onClose: () => void
 * - onOverrideSuccess: (docData) => void — chiamata dopo re-upload success
 */
export default function UploadBlockedModal({
    detail, filename, file, uploadContext, onClose, onOverrideSuccess,
}) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "super_admin" || user?.is_super_admin === true;
    const canOverride = isAdmin && detail?.admin_override_available && file && uploadContext;

    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose, submitting]);

    const matchTypeLabel = {
        piva: "P.IVA rilevata",
        ragione_sociale: "Ragione sociale rilevata",
        both: "P.IVA e ragione sociale rilevate",
    }[detail?.match_type] || "Riferimento rilevato";

    const doOverride = async () => {
        setError(null);
        const clean = reason.trim();
        if (clean.length < 10) {
            setError("Motivazione obbligatoria (min 10 caratteri)");
            return;
        }
        if (submitting) return;
        setSubmitting(true);
        try {
            const { api } = await import("../../lib/api");
            const { toast } = await import("sonner");
            const fd = new FormData();
            fd.append("client_id", uploadContext.clientId);
            fd.append("name", uploadContext.name);
            fd.append("type", uploadContext.type);
            fd.append("visible_to_client", String(uploadContext.visible));
            if (uploadContext.notes) fd.append("notes", uploadContext.notes);
            if (uploadContext.categoryId) fd.append("category_id", uploadContext.categoryId);
            fd.append("override_block", "true");
            fd.append("override_reason", clean);
            fd.append("file", file);
            const { data } = await api.post("/internal/documenti", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.warning("Documento caricato con override admin. Registrato nell'audit log.");
            if (typeof onOverrideSuccess === "function") onOverrideSuccess(data);
            onClose();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : (detail?.message || "Errore override");
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="lt-modal-backdrop" onClick={submitting ? undefined : onClose} data-testid="upload-blocked-backdrop">
            <div
                className="lt-modal"
                onClick={(e) => e.stopPropagation()}
                data-testid="upload-blocked-modal"
                style={{ maxWidth: 620, width: "92vw", maxHeight: "92vh", display: "flex", flexDirection: "column" }}
            >
                <div className="lt-modal__head" style={{ background: "rgba(178,58,72,0.06)", borderBottom: "1px solid rgba(178,58,72,0.3)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <ShieldAlert size={22} strokeWidth={2} style={{ color: "#B23A48" }} />
                        <h2 className="lt-modal__title" style={{ margin: 0, color: "#B23A48" }}>Caricamento bloccato</h2>
                    </div>
                    <button className="lt-icon-btn" onClick={onClose} aria-label="Chiudi" disabled={submitting} data-testid="upload-blocked-close">
                        <X size={16} />
                    </button>
                </div>

                <div style={{ padding: 24, overflowY: "auto", flex: 1, minHeight: 0 }}>
                    <div style={{ marginBottom: 16 }}>
                        <div style={{
                            fontFamily: "IBM Plex Mono, ui-monospace, monospace",
                            fontSize: 11, letterSpacing: 1.6, textTransform: "uppercase",
                            color: "#8A8F88", marginBottom: 4,
                        }}>File</div>
                        <div style={{ fontFamily: "IBM Plex Mono, ui-monospace, monospace", fontSize: 14, color: "#181B20", wordBreak: "break-all" }} data-testid="upload-blocked-filename">
                            {filename}
                        </div>
                    </div>

                    <p style={{ fontSize: 14, color: "#181B20", lineHeight: 1.5, marginBottom: 16 }} data-testid="upload-blocked-message">
                        {detail?.message || "Il documento contiene riferimenti a un cliente diverso. Caricamento bloccato."}
                    </p>

                    {detail?.blocked_client && (
                        <div style={{
                            background: "rgba(178,58,72,0.06)",
                            border: "1px solid rgba(178,58,72,0.3)",
                            borderLeft: "3px solid #B23A48",
                            borderRadius: 3, padding: 14, marginBottom: 16,
                        }} data-testid="upload-blocked-detected">
                            <div style={{
                                fontFamily: "IBM Plex Mono, ui-monospace, monospace",
                                fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase",
                                color: "#8A8F88", marginBottom: 4,
                            }}>{matchTypeLabel}</div>
                            <div style={{ fontSize: 15, fontWeight: 600, color: "#181B20" }} data-testid="upload-blocked-detected-name">
                                {detail.blocked_client.company_name}
                            </div>
                        </div>
                    )}

                    {detail?.evidence && (
                        <details style={{ marginBottom: 16 }} data-testid="upload-blocked-evidence-toggle">
                            <summary style={{ cursor: "pointer", fontSize: 12, color: "#5F6560", fontFamily: "IBM Plex Mono, ui-monospace, monospace", letterSpacing: 0.8, textTransform: "uppercase" }}>
                                Mostra estratto dal documento
                            </summary>
                            <pre style={{
                                marginTop: 8, padding: 12,
                                background: "#F7F7F5", border: "1px solid #E3E5E1", borderRadius: 3,
                                fontSize: 12, lineHeight: 1.5, color: "#5F6560",
                                whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 140,
                                overflowY: "auto",
                                fontFamily: "IBM Plex Mono, ui-monospace, monospace",
                            }} data-testid="upload-blocked-evidence">{detail.evidence}</pre>
                        </details>
                    )}

                    {/* Sezione override admin */}
                    {canOverride ? (
                        <div style={{
                            marginTop: 12, padding: 16,
                            border: "1px dashed rgba(178,122,0,0.5)",
                            background: "rgba(229,178,0,0.05)",
                            borderRadius: 3,
                        }} data-testid="upload-blocked-override-section">
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                                <AlertTriangle size={16} strokeWidth={2} style={{ color: "#B27A00" }} />
                                <div style={{ fontFamily: "IBM Plex Mono, ui-monospace, monospace", fontSize: 12, letterSpacing: 1.2, textTransform: "uppercase", color: "#B27A00", fontWeight: 600 }}>
                                    Forza caricamento (override admin)
                                </div>
                            </div>
                            <p style={{ fontSize: 13, color: "#5F6560", marginBottom: 10, lineHeight: 1.5 }}>
                                Questa azione verrà registrata nell&apos;audit log con la tua motivazione.
                                Il documento sarà marcato &laquo;Da verificare&raquo;.
                            </p>
                            <label style={{ display: "block", fontSize: 12, color: "#5F6560", marginBottom: 6, fontFamily: "IBM Plex Mono, ui-monospace, monospace", textTransform: "uppercase", letterSpacing: 1 }}>
                                Motivazione (min 10 caratteri) *
                            </label>
                            <textarea
                                value={reason}
                                onChange={(e) => { setReason(e.target.value); setError(null); }}
                                rows={3}
                                placeholder="Es. Cliente ha rebrand, beneficiario reale invariato dopo fusione"
                                style={{
                                    width: "100%", padding: 10, fontSize: 13,
                                    border: "1px solid #E3E5E1", borderRadius: 3,
                                    fontFamily: "IBM Plex Mono, ui-monospace, monospace",
                                    resize: "vertical",
                                }}
                                disabled={submitting}
                                data-testid="upload-blocked-override-reason"
                            />
                            {error && (
                                <div style={{ marginTop: 8, fontSize: 12, color: "#B23A48" }} data-testid="upload-blocked-override-error">
                                    {error}
                                </div>
                            )}
                            <div style={{ marginTop: 12, textAlign: "right" }}>
                                <button
                                    type="button"
                                    onClick={doOverride}
                                    disabled={submitting || reason.trim().length < 10}
                                    data-testid="upload-blocked-override-submit"
                                    style={{
                                        padding: "10px 20px",
                                        background: submitting || reason.trim().length < 10 ? "#E3E5E1" : "#B23A48",
                                        color: submitting || reason.trim().length < 10 ? "#8A8F88" : "#fff",
                                        border: "none", borderRadius: 3,
                                        fontFamily: "IBM Plex Mono, ui-monospace, monospace",
                                        fontSize: 12, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase",
                                        cursor: submitting || reason.trim().length < 10 ? "not-allowed" : "pointer",
                                    }}
                                >
                                    {submitting ? "Caricamento…" : "Carica comunque"}
                                </button>
                            </div>
                        </div>
                    ) : isAdmin ? null : (
                        <div style={{ marginTop: 12, padding: 12, background: "#F7F7F5", border: "1px solid #E3E5E1", borderRadius: 3, fontSize: 13, color: "#5F6560" }} data-testid="upload-blocked-no-override-hint">
                            Solo un admin può forzare l&apos;upload. Contatta un amministratore.
                        </div>
                    )}
                </div>

                <div style={{ padding: "16px 24px", borderTop: "1px solid #E3E5E1", display: "flex", justifyContent: "flex-end", gap: 12 }}>
                    <button
                        type="button"
                        className="lt-btn-primary"
                        onClick={onClose}
                        disabled={submitting}
                        data-testid="upload-blocked-ok"
                    >
                        Chiudi
                    </button>
                </div>
            </div>
        </div>
    );
}
